/**
 * AuthComplete - Dedicated OAuth callback landing page
 * 
 * This page handles the handoff from the backend OAuth callback.
 * It is NOT wrapped in BonsaiProvider to avoid race conditions.
 * 
 * Flow:
 * 1. Backend redirects here with ?auth=success&bonsai_user_id=xxx
 * 2. This page stores auth data in sessionStorage
 * 3. Redirects to /hub/dashboard (or the intended destination)
 */

import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

export default function AuthComplete() {
  const [status, setStatus] = useState("Processing sign-in...");
  const [error, setError] = useState("");
  const [officeSignedIn, setOfficeSignedIn] = useState(false);

  useEffect(() => {
    // If inside Office.js dialog, send message to parent and close
    if (typeof Office !== 'undefined' && Office.context?.ui) {
      const params = new URLSearchParams(window.location.search);
      const userId = params.get('bonsai_user_id');
      if (userId) {
        Office.onReady(() => {
          Office.context.ui.messageParent(JSON.stringify({ type: 'auth_success', userId }));
        });
        setOfficeSignedIn(true);
        return;
      }
    }
  }, []);

  useEffect(() => {
    if (officeSignedIn) return;
    const urlParams = new URLSearchParams(window.location.search);
    const authSuccess = urlParams.get("auth");
    const bonsaiUserId = urlParams.get("bonsai_user_id");
    const destination = urlParams.get("destination") || "/hub/dashboard";


    if (authSuccess === "success" && bonsaiUserId) {
      // Store auth state in both sessionStorage and localStorage (for cross-tab persistence)
      sessionStorage.setItem("bonsai_user_id", bonsaiUserId);
      localStorage.setItem("bonsai_user_id", bonsaiUserId);
      sessionStorage.setItem("bonsai_auth", "true");
      localStorage.setItem("bonsai_auth", "true");
      sessionStorage.setItem("bonsai_last_activity", Date.now().toString());
      localStorage.setItem("bonsai_last_activity", Date.now().toString());

      setStatus("Sign-in successful! Redirecting...");

      // Small delay to ensure sessionStorage is flushed, then redirect
      setTimeout(() => {
        window.location.href = destination;
      }, 100);
    } else if (urlParams.get("error")) {
      const errorMsg = urlParams.get("error_description") || urlParams.get("error") || "Authentication failed";
      console.error("[AuthComplete] Auth error:", errorMsg);
      setError(errorMsg);
      setStatus("");
    } else {
      setError("No authentication data received. Redirecting to login...");
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
    }
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F8F8]" data-testid="auth-complete-page">
      <div className="text-center p-8 glass-card max-w-md w-full mx-4">
        {error ? (
          <>
            <div className="w-16 h-16 rounded-md bg-status-red-dim flex items-center justify-center mx-auto mb-4">
              <span className="text-status-red text-2xl">!</span>
            </div>
            <h2 className="text-lg font-semibold text-white mb-2">Sign-in Problem</h2>
            <p className="text-sm text-status-red mb-4" data-testid="auth-error-message">{error}</p>
            <a
              href="/login"
              className="inline-block px-4 py-2 bg-teal text-white rounded-md text-sm hover:bg-teal-hover transition-colors"
              data-testid="back-to-login-link"
            >
              Back to Sign In
            </a>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 text-teal animate-spin mx-auto mb-4" />
            <p className="text-txt-muted font-medium" data-testid="auth-status-message">{status}</p>
          </>
        )}
      </div>
    </div>
  );
}
